/**
 * Generates or retrieves a stable contact UUID for the widget.
 * Uses localStorage (primary), cookie (fallback), or in-memory (last resort).
 * 
 * @returns {string} A UUID v4 string, always returns a value (never throws)
 */

let inMemoryUuid = null;
const STORAGE_KEY = 'widget_contact_uuid';
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value) {
	return typeof value === 'string' && UUID_V4_REGEX.test(value);
}

function fallbackUUID() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = Math.random() * 16 | 0;
		const v = c === 'x' ? r : (r & 0x3 | 0x8);
		return v.toString(16);
	});
}

export function generateUUIDv4() {
	if (typeof crypto === 'undefined') return fallbackUUID();
	if (crypto.randomUUID) return crypto.randomUUID();

	if (crypto.getRandomValues) {
		const bytes = new Uint8Array(16);
		crypto.getRandomValues(bytes);

		bytes[6] = (bytes[6] & 0x0f) | 0x40;
		bytes[8] = (bytes[8] & 0x3f) | 0x80;

		return [...bytes]
			.map((b, i) => ([4, 6, 8, 10].includes(i) ? '-' : '') + b.toString(16).padStart(2, '0'))
			.join('');
	}

	return fallbackUUID();
}

function getCookie(name) {
	try {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			return parts.pop().split(';').shift();
		}
	} catch (e) { }
	return null;
}

function setCookie(name, value, maxAge = 31536000) {
	try {
		document.cookie = `${name}=${value}; Path=/; Max-Age=${maxAge}; SameSite=None; Secure; Partitioned`;
	} catch (e) { }
}

function getLocalStorage(key) {
	try {
		return localStorage.getItem(key);
	} catch (e) { }
	return null;
}

function setLocalStorage(key, value) {
	try {
		localStorage.setItem(key, value);
	} catch (e) { }
}

export function getOrCreateContactUuid() {
	let uuid = getLocalStorage(STORAGE_KEY);

	if (!isValidUuid(uuid)) {
		uuid = getCookie(STORAGE_KEY);
	}

	if (!isValidUuid(uuid)) {
		uuid = inMemoryUuid;
	}

	if (!isValidUuid(uuid)) {
		uuid = generateUUIDv4();
	}

	inMemoryUuid = uuid;
	setLocalStorage(STORAGE_KEY, uuid);
	setCookie(STORAGE_KEY, uuid);

	return uuid;
}
